import { careModelAnalytics } from 'src/services/analytics';
import { computed, ref } from 'vue';

import { createAuditFeedback } from '../../application/useCases/createAuditFeedback';
import { FEEDBACK_STANCES } from '../../domain/constants/auditResults';
import { removeAuditFromSessionActivity, submitAuditFeedback } from '../../infrastructure/api/auditApi';

/** @typedef {import('../../domain/entities/Audit.types').AuditVerdict} AuditVerdict */
/** @typedef {import('../../domain/entities/AuditLearning.types').FeedbackLearningOutcome} FeedbackLearningOutcome */

/**
 * This composable abstracts the logic related to submitting feedback on an audit result.
 *
 * @param {Object|Function} sessionActivitySource - The session activity object (or getter) containing the audit result.
 */
// eslint-disable-next-line import/prefer-default-export
export function useAuditFeedback(sessionActivitySource) {
  const sessionActivity = computed(
    () => (typeof sessionActivitySource === 'function'
      ? sessionActivitySource()
      : sessionActivitySource),
  );

  const isProcessing = ref(false);
  const feedbackPending = ref(false);
  const hasFeedback = computed(
    () => sessionActivity.value.audit?.hasFeedback || feedbackPending.value,
  );

  // Wire use case to infrastructure
  const feedbackUseCase = createAuditFeedback(
    submitAuditFeedback,
    removeAuditFromSessionActivity,
  );

  /**
   * Marks the audit result as accurate by submitting feedback with an 'accurate' stance.
   */
  async function markAccurate() {
    isProcessing.value = true;
    try {
      careModelAnalytics.trackAuditActionFeedbackSubmitted(
        sessionActivity.value.id,
        sessionActivity.value.audit.verdict,
        FEEDBACK_STANCES.ACCURATE,
      );
      await feedbackUseCase.markAccurate(sessionActivity.value);
      feedbackPending.value = true;
    } finally {
      isProcessing.value = false;
    }
  }

  /**
   * Marks the audit result as inaccurate by submitting feedback with an 'inaccurate' stance.
   * @param {string|null} comment - The user's comment providing additional context or explanation.
   * @param {AuditVerdict|null} correctedVerdict - The user's corrected verdict if the audit result is inaccurate.
   * @returns {Promise<FeedbackLearningOutcome|null>}
   */
  async function markInaccurate(comment, correctedVerdict) {
    isProcessing.value = true;
    try {
      careModelAnalytics.trackAuditActionFeedbackSubmitted(
        sessionActivity.value.id,
        sessionActivity.value.audit.verdict,
        FEEDBACK_STANCES.INACCURATE,
        correctedVerdict,
      );
      return await feedbackUseCase.markInaccurate(sessionActivity.value, comment, correctedVerdict);
    } finally {
      isProcessing.value = false;
    }
  } // markInaccurate()

  /**
   * Removes the audit result from the session activity, effectively clearing the audit. This does not delete the
   * logged audit or feedback, but simply disassociates it from the session activity so it no longer appears in the UI.
   */
  async function removeAuditResult() {
    isProcessing.value = true;
    try {
      await feedbackUseCase.removeAuditResult(sessionActivity.value);
    } finally {
      isProcessing.value = false;
    }
  } // removeAuditResult()

  return {
    isProcessing,
    hasFeedback,
    markAccurate,
    markInaccurate,
    removeAuditResult,
  };
}
